import WebsiteScreen from "../WebsiteScreen";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { useSelector } from "react-redux";
import { Col, Container, Modal, Placeholder, Row, Form, Button, Tooltip, OverlayTrigger } from "react-bootstrap";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import axios from "axios";

import alertIcon from "../../Assets/img/alert-icon.svg";
import aioaBanner from "../../Assets/img/AIOA-Banner-3.webp";
import manualBanner from "../../Assets/img/Manual-Accessibility-Audit-Report.webp";
import ScanningMonitoringBanner from "../../Assets/img/Accessibility-Monitoring-banner-2.webp";


interface PropType {
    accessibilitySystemDetected: string,
    accessibilityScore: number,
    errorViolationsTotal: number,
    errorViolationsTotalFixedByWidget: number,
    fixedByWidget: boolean,
    setFixedByWidget: Function,
    isWhiteLabel: boolean,
}

interface ForwardedRefs {
    scoreRef: React.RefObject<HTMLDivElement>;
    wcagRef: React.RefObject<HTMLDivElement>;
}

// component to render scanning monitoring banner
const ScanningMonitoringPromoBanner = () => {
    return (
        <div className="aioa-promo-banner">
            <a href="https://skynetaccessibilityscan.com/trial-subscription" target="_blank">
                <img src={ScanningMonitoringBanner} alt="Get Started with Scanning and Monitoring" className="img-fluid" />
            </a>

        </div>
    )
}


// component to render all in one promotional banner
const AIOAPromoBanner = () => {
    return (
        <div className="aioa-promo-banner">
            <a href="https://ada.skynettechnologies.us/trial-subscription" target="_blank">
                <img src={aioaBanner} alt="Get All in One Accessibility" className="img-fluid" />
            </a>
        </div>
    )
}

// component to render manual accessibility audit promo banner
const ManualAuditBanner = () => {
    return (
        <div className="aioa-promo-banner">
            <a href="https://www.skynettechnologies.com/website-accessibility-audit" target="_blank">
                <img src={manualBanner} alt="Manual Accessibility Audit" className="img-fluid" />
            </a>
        </div>
    )
}


// component to render top section of scan results
const WebsiteURLSection = forwardRef<ForwardedRefs, PropType>(
    ({ accessibilitySystemDetected, accessibilityScore, errorViolationsTotal, errorViolationsTotalFixedByWidget, fixedByWidget, setFixedByWidget, isWhiteLabel }, ref) => {

        const { t, i18n } = useTranslation();

        const switchRef = useRef<HTMLInputElement>(null);

        const { isPaidFree, standard, levelFailCounts, levelFailCountsFixedByWidget } = useSelector((store: StoreProptypes) => store);

        const [limitations, setLimitations] = useState('');

        const [scoreDescription, setScoreDescription] = useState('');


        const [showLimitationsModal, setShowLimitationsModal] = useState(false);
        const [showScoreDescriptionModal, setShowScoreDescriptionModal] = useState(false);

        const getLimitationsApi = axios.get(`${process.env.REACT_APP_BASE_URL}/public/translations/limitations/${i18n.language}.html`);

        const getScoreDescriptionApi = axios.get(`${process.env.REACT_APP_BASE_URL}/public/translations/accessibility-score/${i18n.language}.html`);

        const [levelA, setLevelA] = useState<number>(0);
        const [levelAA, setLevelAA] = useState<number>(0);
        const [levelAAA, setLevelAAA] = useState<number>(0);


        useEffect(() => {
            // check if show limitations flag available
            if (showLimitationsModal) {
                setLimitations("");

                // api to get limitations html
                getLimitationsApi.then(response => {
                    if (response.data) {
                        setLimitations(response.data);
                    }
                })

            }
        }, [showLimitationsModal]);


        useEffect(() => {
            // check if flag available to display accessibility score information
            if (showScoreDescriptionModal) {
                setScoreDescription("");

                // api to get score description html
                getScoreDescriptionApi.then(response => {
                    if (response.data) {
                        setScoreDescription(response.data);
                    }
                })

            }
        }, [showScoreDescriptionModal])


        useEffect(() => {
            // check if level wise violation counts are available
            if (levelFailCountsFixedByWidget && levelFailCounts) {
                let tempA, tempAA, tempAAA: number;

                // check if fixed by widget flag is available
                if (fixedByWidget) {
                    tempA = levelFailCounts.A - levelFailCountsFixedByWidget.A;
                    tempAA = levelFailCounts.AA - levelFailCountsFixedByWidget.AA;
                    tempAAA = levelFailCounts.AAA - levelFailCountsFixedByWidget.AAA;
                }
                else {
                    tempA = levelFailCounts.A;
                    tempAA = levelFailCounts.AA;
                    tempAAA = levelFailCounts.AAA;
                }

                const total = tempA + tempAA + tempAAA;

                setLevelA(tempA / total * 100);
                setLevelAA(tempAA / total * 100);
                setLevelAAA(tempAAA / total * 100);

            }

        }, [levelFailCounts, levelFailCountsFixedByWidget, fixedByWidget])

        useEffect(() => {
            const el = switchRef.current;

            if (!el) return;

            const handler = (e: KeyboardEvent) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    setFixedByWidget((prev:boolean) => !prev);
                }
            };

            el.addEventListener("keydown", handler);

            return () => {
                el.removeEventListener("keydown", handler);
            };
        }, []);


        const scoreLocalRef = useRef<HTMLDivElement>(null);
        const wcagLocalRef = useRef<HTMLDivElement>(null);

        // function to save references
        useImperativeHandle(ref, () => ({
            scoreRef: scoreLocalRef,
            wcagRef: wcagLocalRef,
        }));

        return (

            <div className="website-screen-details-wrap">

                <Container>

                    {(accessibilitySystemDetected !== ''
                        && accessibilitySystemDetected !== null
                        && accessibilitySystemDetected !== "All in One Accessibility™") // check if any accessibility system is detected, but its not all in one accessibility.
                        ? <div className="system-detected">
                            {t("Overlay Detected")}
                        </div>
                        : (
                            <>
                                {isPaidFree ? // check if all in one accessibility widget is detected
                                    (
                                        <>
                                            {isPaidFree === 2 ? // check if all in one accessibility paid widget is detected
                                                <>
                                                    <div className={`system-detected ${isWhiteLabel ? 'mb-2' : ''}`}>
                                                        <span> {t("All in One Accessibility Detected")} </span>
                                                    </div>
                                                </>
                                                : <>
                                                    <div className={`system-detected ${fixedByWidget || isWhiteLabel ? 'mb-2' : ''}`}>
                                                        <Form.Check
                                                            ref={switchRef}
                                                            type="switch"
                                                            id="custom-switch"
                                                            label={t("All in One Accessibility Free Version Detected")}
                                                            className="form-check-reverse text-start w-100"
                                                            checked={fixedByWidget}
                                                            onChange={() => setFixedByWidget(!fixedByWidget)}
                                                            tabIndex={0} // ensures focusability if needed
                                                        />
                                                    </div>
                                                </>}

                                        </>
                                    )
                                    : (
                                        <>
                                            <div className={`system-detected ${fixedByWidget || isWhiteLabel ? 'mb-2' : ''}`}>
                                                <Form.Check
                                                    ref={switchRef}
                                                    type="switch"
                                                    id="custom-switch"
                                                    label={t("No Overlay Detected")}
                                                    className="form-check-reverse text-start w-100"
                                                    checked={fixedByWidget}
                                                    onChange={() => setFixedByWidget(!fixedByWidget)}
                                                    tabIndex={0} // ensures focusability if needed
                                                />
                                            </div>
                                        </>
                                    )}
                            </>
                        )}

                    {fixedByWidget && !isWhiteLabel ? // check if fixed by widget flag is available
                        <div className="accessibility-score-notice text-danger fw-medium">
                            {/* {t("Score Disclaimer")} */}
                            <Trans i18nKey="Score Disclaimer">
                                The accessibility score displayed here assumes that you have the All in One Accessibility Paid version installed. <a href="https://ada.skynettechnologies.us/trial-subscription">Try Now</a>
                            </Trans>
                        </div> : <></>}


                    <div className="website-screen-details">

                        <Row xs={1} lg={3}>
                            <Col className="mb-4 mb-lg-0"> <div className="h-100"> <WebsiteScreen /> </div></Col>
                            <Col className="mb-4 mb-lg-0">
                                <div className="h-100">
                                    <div className="aioa_dashboard-violations-graph-box" ref={scoreLocalRef}>
                                        <h2 className="aioa_dashboard-violations-graph-box-title h5">{t('Accessibility Score')}
                                            <Button variant="link" aria-label={t("Accessibility Score Help")} className="p-0 ms-1" onClick={() => setShowScoreDescriptionModal(true)}>
                                                 <OverlayTrigger
                                                    delay={{ show: 250, hide: 400 }}
                                                    overlay={<Tooltip>
                                                        <Trans
                                                            i18nKey="How Accessibility Score is Calculated"
                                                        />
                                                    </Tooltip>}>
                                                    <img src={alertIcon} tabIndex={0} alt={""} style={{ width: 20 }} />
                                                </OverlayTrigger>
                                                {/* <img src={alertIcon} alt="" style={{ width: 25 }} /> */}
                                            </Button>
                                        </h2>
                                        <div className="aioa_dashboard-violations-semi-donut-graph" style={{ "--percentage": accessibilityScore, "--fill": '#4A6681' } as React.CSSProperties}>
                                            <div className="aioa_dashboard-violations-graph-box-per h1 fw-bold">{accessibilityScore.toFixed(2)}%</div>
                                            <div className={`aioa_dashboard-violations-graph-box-info h6 mb-0 ${accessibilityScore > 85 ? `text-success success` : accessibilityScore > 49 ? `text-warning warning semi-comliant-warning-color` : `text-danger danger`}`}>
                                                {accessibilityScore > 85 ? t("Compliant") : accessibilityScore > 49 ? t("Semi Compliant") : t("Not Compliant")}
                                            </div>
                                        </div>
                                        <div className="mt-0 mt-lg-3 score-limitation-wrapper"> <Trans i18nKey='Accessibility Score Limitation Note'> Automated Accessibility score has limitations, <br></br> <a role="button" href="#" aria-haspopup="true" className="text-underline text-info" onClick={() => setShowLimitationsModal(true)}> Learn more about these limitations </a> </Trans></div>
                                    </div>
                                </div>
                            </Col>
                            <Col>

                                <div className="h-100">
                                    <div className="aioa_dashboard-violations-graph-box levels-graph" ref={wcagLocalRef}>
                                        <h2 className="aioa_dashboard-violations-graph-box-title h5">{t('WCAG 2.0, 2.1 & 2.2')}</h2>
                                        <div className="aioa_dashboard-violations-semi-donut-graph" style={{ "--percentage": accessibilityScore, "--fill": '#4A6681' } as React.CSSProperties}>
                                            <div className="graph" data-name="A" style={{ "--percentage": '100', "--d-percentage": Math.round(levelA), "--fill": '#CADAEA' } as React.CSSProperties}></div>
                                            <div className="graph" data-name="AA" style={{ "--percentage": Math.round(levelAAA + levelAA), "--d-percentage": Math.round(levelAA), "--fill": '#92B6D8' } as React.CSSProperties}></div>
                                            <div className="graph" data-name="AAA" style={{ "--percentage": Math.round(levelAAA), "--d-percentage": Math.round(levelAAA), "--fill": '#4A6681' } as React.CSSProperties}></div>
                                            <div className="d-flex flex-column" aria-label={`${fixedByWidget 
      ? (errorViolationsTotal - errorViolationsTotalFixedByWidget) 
      : errorViolationsTotal} ${t("Failed Checks")}`}>
                                                <div className="aioa_dashboard-violations-graph-box-per h1 fw-bold">
                                                    {fixedByWidget ? (errorViolationsTotal - errorViolationsTotalFixedByWidget) : errorViolationsTotal}
                                                </div>
                                                <div className={`aioa_dashboard-violations-graph-box-info h6 mb-0 text-danger`}>
                                                    {t("Failed Checks")}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-0 mt-lg-2">
                                            <div className="levels-legend">
                                                <div className="level" data-percentage={Math.round(levelA)}>A</div>
                                                <div className="level" data-percentage={Math.round(levelAA)}>AA</div>
                                                <div className="level" data-percentage={Math.round(levelAAA)}>AAA</div>
                                            </div>
                                        </div>
                                        <div className="mt-2 wcag-compliance-summary">
                                            {accessibilityScore > 85 ? <Trans
                                                i18nKey="Compliant under"
                                                defaults="Compliant under <bold>{{- standard}}</bold>"
                                                values={{
                                                    standard: standard,
                                                }}
                                                components={{
                                                    bold: <strong />,
                                                }}
                                            /> : accessibilityScore > 49 ? <Trans
                                                i18nKey="Semi compliant under"
                                                defaults="Semi compliant under <bold>{{- standard}}</bold>"
                                                values={{
                                                    standard: standard,
                                                }}
                                                components={{
                                                    bold: <strong />,
                                                }}
                                            /> : <Trans
                                                i18nKey="Not compliant under"
                                                defaults="Not compliant under <bold>{{- standard}}</bold>"
                                                values={{
                                                    standard: standard,
                                                }}
                                                components={{
                                                    bold: <strong />,
                                                }}
                                            />}

                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>


                    </div>

                    {/* {(accessibilitySystemDetected !== ''
                        && accessibilitySystemDetected !== null
                        && accessibilitySystemDetected !== "All in One Accessibility™") // check if all in one accessibility is detected
                        ?
                        <>
                            {!isWhiteLabel && (
                                <ManualAuditBanner />
                            )}
                        </>
                        : (
                            <>
                                {isPaidFree ? // check if widget flag is available
                                    (
                                        <>
                                            {isPaidFree === 2 // check if widget paid
                                                ? <>
                                                    {!isWhiteLabel && (
                                                        // <ScanningMonitoringPromoBanner />
                                                        <ManualAuditBanner />
                                                    )}
                                                </> : <>
                                                    {!isWhiteLabel && (
                                                        <AIOAPromoBanner />
                                                    )}
                                                </>}

                                        </>
                                    ) : (
                                        <>
                                            {!isWhiteLabel && (
                                                <AIOAPromoBanner />
                                            )}
                                        </>
                                    )}
                            </>
                        )} */}
                    {!isWhiteLabel && (
                        <section className="container my-5">
                            <div className="p-4 rounded-4 shadow-lg text-white"
                                style={{background:"linear-gradient(90deg, var(--accessibility-checker-primary-color, rgb(46 28 65)) 0%, color-mix(in srgb, var(--accessibility-checker-primary-color, rgb(66 0 131)) 70%, white) 60%)"}}>
                                
                                <div className="row align-items-center g-4">
                                
                                <div className="col-md-9 d-flex align-items-start">
                                    {/* <div className="flex-shrink-0 bg-white bg-opacity-25 p-3 rounded-3 me-3 d-flex align-items-center justify-content-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="none" stroke="currentColor"
                                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="text-white">
                                        <path d="M12 9v2m0 4h.01M4.93 4.93l14.14 14.14M12 2a10 10 0 100 20 10 10 0 000-20z"/>
                                    </svg>
                                    </div> */}

                                    <div>
                                    <h2 className="fw-semibold mb-2 fs-3">Head's up</h2>
                                    <p className="mb-0 text-white-75 lh-sm">
                                        Skynet Accessibility free checker testing tool checks one page at a time, Accessibility Compliance requires all the website pages to be compliant as per WCAG 2.1 / 2.2 level AA standard on entire website. Run a full website scan to find issues across all pages and user flows.
                                    </p>
                                    </div>
                                </div>

                                <div className="col-md-3 text-md-end">
                                    <a href="https://skynetaccessibilityscan.com/trial-subscription"
                                    target="_blank"
                                    className="btn btn-light fw-semibold px-4 py-2 rounded-3 text-purple"
                                    style={{color:"#6d28d9"}}>
                                        Scan your website
                                    </a>
                                </div>
                                </div>
                            </div>
                        </section>
                    )}


                </Container>
                <Modal show={showLimitationsModal} onHide={() => setShowLimitationsModal(false)} animation={false} size="lg" centered>
                    <Modal.Body>
                        <button className="modal-close-button" aria-label={t("Close Modal Popup")} onClick={() => setShowLimitationsModal(false)}></button>
                        <div className="px-5 py-3">
                            <h2 className="text-center h4 text-primary fw-bold mb-4">{t("Automated Accessibility Score Limitations")}</h2>
                            {limitations ? <div dangerouslySetInnerHTML={{ __html: limitations }}></div> :
                                <Placeholder animation="glow" className="">
                                    <Placeholder />
                                    <Placeholder style={{ width: "80%" }} />
                                    <Placeholder style={{ width: "50%" }} />
                                    <Placeholder style={{ width: "30%" }} />
                                    <Placeholder />
                                    <Placeholder style={{ width: "80%" }} />
                                    <Placeholder />
                                    <Placeholder style={{ width: "30%" }} />
                                    <Placeholder style={{ width: "50%" }} />
                                    <Placeholder />
                                    <Placeholder style={{ width: "50%" }} />
                                    <Placeholder style={{ width: "40%" }} />
                                    <Placeholder style={{ width: "75%" }} />
                                    <Placeholder />
                                    <Placeholder style={{ width: "30%" }} />
                                    <Placeholder style={{ width: "80%" }} />
                                    <Placeholder />
                                    <Placeholder style={{ width: "50%" }} />
                                    <Placeholder />
                                    <Placeholder style={{ width: "20%" }} />
                                    <Placeholder style={{ width: "70%" }} />
                                </Placeholder>
                            }
                        </div>
                    </Modal.Body>
                </Modal>

                <Modal show={showScoreDescriptionModal} onHide={() => setShowScoreDescriptionModal(false)} animation={false} size="lg" centered>
                    <Modal.Body>
                        <button className="modal-close-button" aria-label={t("Close Modal Popup")} onClick={() => setShowScoreDescriptionModal(false)}></button>
                        <div className="px-5 py-3">
                            <h2 className="text-center h4 text-primary fw-bold mb-4">{t("How Accessibility Score is Calculated?")}</h2>
                            {scoreDescription ? <div dangerouslySetInnerHTML={{ __html: scoreDescription }}></div> :
                                <Placeholder animation="glow" className="">
                                    <Placeholder />
                                    <Placeholder style={{ width: "80%" }} />
                                    <Placeholder style={{ width: "50%" }} />
                                    <Placeholder style={{ width: "30%" }} />
                                    <Placeholder />
                                    <Placeholder style={{ width: "80%" }} />
                                    <Placeholder />
                                    <Placeholder style={{ width: "30%" }} />
                                    <Placeholder style={{ width: "50%" }} />
                                    <Placeholder />
                                    <Placeholder style={{ width: "50%" }} />
                                    <Placeholder style={{ width: "40%" }} />
                                    <Placeholder style={{ width: "75%" }} />
                                    <Placeholder />
                                    <Placeholder style={{ width: "30%" }} />
                                    <Placeholder style={{ width: "80%" }} />
                                    <Placeholder />
                                    <Placeholder style={{ width: "50%" }} />
                                    <Placeholder />
                                    <Placeholder style={{ width: "20%" }} />
                                    <Placeholder style={{ width: "70%" }} />
                                </Placeholder>
                            }


                        </div>
                    </Modal.Body>
                </Modal>
            </div>
        )
    }
);

export default WebsiteURLSection;
